// common
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store";

// interfaces
import { IAppState } from "@/interfaces/store/IApp";

// helpers
import { getLanguage, setLanguage } from "@/utils/helpers/lang";
import { LanguagesType } from "@/types/app";
import dayjs from "dayjs";

@Module({ dynamic: true, namespaced: true, store, name: "App" })
class App extends VuexModule implements IAppState {
  public language = getLanguage();
  public gid: number | null = null;
  public isUpdateAvailable = false;
  public isRefreshing = false;
  public serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
  public lastUpdateDate = dayjs().toISOString();
  public autoUpdateCount = 0;
  public isLoading = false;
  public debugMode = false;

  // list of userIds who already seen and dismissed the release banner so we don't show it again
  public dismissedReleaseBannerForUserIds: number[] = [];

  // background loading: used to show a loading bar in the navbar
  public isBackgroundLoading = false;

  public get getLanguageTag() {
    const TAGS_MAP = {
      en: "en-US",
      de: "de-DE",
      it: "it-IT",
      fr: "fr-FR",
      es: "es-ES",
    };
    return TAGS_MAP[this.language];
  }

  public get appVersion() {
    return import.meta.env.VITE_APP_VERSION;
  }

  @Action
  public SetLanguage(language: LanguagesType) {
    this.SET_LANGUAGE(language);
  }

  @Mutation
  public setGID(gid: number) {
    this.gid = gid;
  }

  @Mutation
  public removeGID() {
    this.gid = null;
  }

  @Mutation
  private SET_LANGUAGE(language: LanguagesType) {
    this.language = language;
    setLanguage(this.language);
  }

  @Mutation
  public setIsUpdateAvailable(status: boolean) {
    this.isUpdateAvailable = status;
  }

  @Mutation
  public setIsRefreshing(status: boolean) {
    this.isRefreshing = status;
  }

  @Mutation
  public setServiceWorkerRegistration(sw: ServiceWorkerRegistration) {
    this.serviceWorkerRegistration = sw;
  }

  @Mutation
  public setLastUpdateDate(date: string) {
    this.lastUpdateDate = date;
  }

  @Mutation
  public setAutoUpdateCount(count: number) {
    this.autoUpdateCount = count;
  }

  @Mutation
  updatedIsLoading(value: boolean) {
    this.isLoading = value;
  }

  @Mutation
  setIsBackgroundLoading(value: boolean) {
    this.isBackgroundLoading = value;
  }

  @Mutation
  setDebugMode(value: boolean) {
    this.debugMode = value;
  }

  @Mutation
  addDismissedReleaseBannerForUserId(userId: number) {
    if (!this.dismissedReleaseBannerForUserIds.includes(userId)) {
      this.dismissedReleaseBannerForUserIds.push(userId);
    }
  }
  /**
   * Resotres app module fields saved in localstorage
   */
  @Mutation
  restore(appFields: Partial<IAppState>) {
    const {
      gid,
      autoUpdateCount,
      lastUpdateDate,
      dismissedReleaseBannerForUserIds,
    } = appFields;

    if (gid) {
      this.gid = gid;
    }
    if (autoUpdateCount) {
      this.autoUpdateCount = autoUpdateCount;
    }
    if (lastUpdateDate) {
      this.lastUpdateDate = lastUpdateDate;
    }

    if (
      dismissedReleaseBannerForUserIds &&
      Array.isArray(dismissedReleaseBannerForUserIds)
    ) {
      this.dismissedReleaseBannerForUserIds = dismissedReleaseBannerForUserIds;
    }
  }
}

const AppModule = getModule(App);
export default AppModule;
